import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Ticket, Copy, Sparkles, Percent } from "lucide-react";

const COUPONS = {
  active: [
    { code: "SAVE20", off: "20% OFF", desc: "Get 20% OFF on your order", min: 999, max: 500, expiry: "25 May 2025", best: true },
    { code: "HEALTH15", off: "15% OFF", desc: "Get 15% OFF on your order", min: 799, max: 400, expiry: "30 May 2025" },
    { code: "FLAT200", off: "₹200 OFF", desc: "Flat ₹200 OFF on your order", min: 999, max: 200, expiry: "05 Jun 2025" },
    { code: "WELLNESS10", off: "10% OFF", desc: "Get 10% OFF on wellness products", min: 499, max: 250, expiry: "10 Jun 2025" },
  ],
  used: [],
  expired: [],
};

const TABS = [
  { id: "active", label: "Active", count: 6 },
  { id: "used", label: "Used", count: 8 },
  { id: "expired", label: "Expired", count: 4 },
];

export default function Coupons() {
  const [tab, setTab] = useState("active");
  const list = useMemo(() => COUPONS[tab] || [], [tab]);

  const copy = (code) => {
    try {
      navigator.clipboard?.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.success("Copied");
    }
  };

  const best = COUPONS.active.find((c) => c.best);

  return (
    <div data-testid="account-coupons" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          My Coupons
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Save more on your health &amp; wellness shopping
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4">
          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <div className="flex gap-1 bg-[#F5F3FB] rounded-xl p-1 w-fit">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`coupon-tab-${t.id}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                    tab === t.id
                      ? "bg-white text-[#4B2AAD] shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {t.label} ({t.count})
                </button>
              ))}
            </div>
            <h3 className="mt-4 font-display text-base font-bold text-gray-900">
              Your Coupon Wallet
            </h3>

            <div className="mt-3 space-y-3">
              {list.map((c) => (
                <div
                  key={c.code}
                  className="rounded-2xl border border-gray-100 p-4 flex items-center gap-4 relative overflow-hidden"
                >
                  <div className="h-24 w-20 shrink-0 rounded-xl bg-[#F3EFFC] flex flex-col items-center justify-center relative">
                    <span className="text-[9px] font-bold uppercase text-[#4B2AAD]/80 tracking-wider">
                      Save
                    </span>
                    <span className="text-lg font-display font-extrabold text-[#4B2AAD] text-center px-1">
                      {c.off}
                    </span>
                    {c.best && (
                      <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-[#FFB020] text-[#111827] text-[9px] font-bold px-2 py-0.5">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display text-base font-bold text-gray-900">
                        {c.code}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{c.desc}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Min. order value ₹{c.min}
                    </p>
                    <button className="text-[11px] text-[#4B2AAD] font-semibold hover:underline mt-1">
                      View T&C
                    </button>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-gray-500">Expires on</p>
                    <p className="text-sm font-bold text-gray-900">{c.expiry}</p>
                    <p className="text-[10px] text-gray-500 mt-2">
                      Max discount <span className="font-bold text-gray-700">₹{c.max}</span>
                    </p>
                    <button
                      type="button"
                      onClick={() => copy(c.code)}
                      className="mt-2 inline-flex items-center gap-1 rounded-xl bg-[#4B2AAD] text-white text-xs font-bold px-3 py-1.5 hover:bg-[#361D82]"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy Code
                    </button>
                  </div>
                </div>
              ))}
              {list.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                  <Ticket className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    No coupons in this bucket right now.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-[#F5F3FB] border border-gray-100 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-white flex items-center justify-center text-[#4B2AAD] shrink-0 shadow-sm border border-[#4B2AAD]/10">
                <Ticket className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">How to use coupons?</p>
                <p className="text-xs text-gray-600 mt-0.5 flex flex-wrap items-center gap-1.5">
                  <span>Add products to cart</span>
                  <span className="text-gray-400 font-normal">&rarr;</span>
                  <span>Apply coupon at checkout</span>
                  <span className="text-gray-400 font-normal">&rarr;</span>
                  <span>Enjoy savings!</span>
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-[#4B2AAD] hover:underline whitespace-nowrap flex items-center gap-1">
              View All Terms &amp; Conditions <span className="text-sm font-normal">&rsaquo;</span>
            </button>
          </div>
        </div>

        {/* Best Coupon */}
        <aside className="space-y-4">
          <div className="rounded-2xl bg-white border border-[#4B2AAD]/25 p-5">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#4B2AAD]" />
              <h4 className="text-sm font-bold text-gray-900">Best Coupon for You</h4>
              <span className="ml-auto inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold px-2 py-0.5 border border-emerald-100">
                Auto Applied
              </span>
            </div>
            {best && (
              <div className="mt-3 rounded-xl bg-[#F3EFFC] p-4 text-center">
                <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider">
                  {best.code}
                </p>
                <p className="mt-1 text-2xl font-display font-extrabold text-[#4B2AAD]">
                  {best.off}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {best.desc}. Min. order value ₹{best.min}
                </p>
                <div className="mt-3 text-xs text-emerald-700 font-semibold flex items-center justify-center gap-1">
                  <Percent className="h-3.5 w-3.5" />
                  Will be auto-applied at checkout
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
